/**
 * Wizard de criacao de usuario — /configuracoes/acessos/usuarios/novo.
 *
 * 3 passos (Sprint 6 do plano):
 *   1) Dados basicos (nome, email)
 *   2) Cargo + preview de permissoes
 *   3) Confirmacao + opcao "enviar convite por email" (default) ou
 *      "gerar senha provisoria" (fallback)
 *
 * Apos criar, redireciona para a ficha do novo usuario. Se senha
 * provisoria, exibe-a UMA UNICA vez antes de redirecionar.
 *
 * P5 (anti monolito): cada passo tem ~3 campos. P10 (microfeedback):
 * cada passo valida antes de avancar. P2 (visibilidade): badge mostra
 * qual passo esta ativo.
 */
import React, { useEffect, useState } from "react";
import { FiArrowLeft, FiArrowRight, FiCheckCircle, FiCopy, FiMail } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";

import { criarUsuario, enviarConvite, fetchCargos } from "../api";
import {
  ActionsBar,
  Blocks,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  ErrorMsg,
  Field,
  InfoMsg,
  InlineForm,
  Input,
  Pill,
  PrimaryButton,
  SecondaryButton,
  Select,
} from "../styles.jsx";

const PASSOS = ["Dados", "Cargo", "Confirmação"];

function StepIndicator({ atual }) {
  return (
    <ActionsBar style={{ marginBottom: 16 }}>
      {PASSOS.map((label, idx) => (
        <Pill
          key={label}
          $tone={idx === atual ? "info" : idx < atual ? "success" : "neutral"}
        >
          {idx + 1}. {label}
        </Pill>
      ))}
    </ActionsBar>
  );
}

function UsuarioNovo() {
  const navigate = useNavigate();
  const { request } = useAuth();

  const [passo, setPasso] = useState(0);
  const [cargos, setCargos] = useState([]);
  const [dados, setDados] = useState({
    nome: "",
    email: "",
    cargo_id: "",
    cargo_exibido: "",
    enviar_convite: true,
  });
  const [error, setError] = useState(null);
  const [criando, setCriando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const payload = await fetchCargos(request, false);
        setCargos(payload?.resultados || []);
      } catch {
        setCargos([]);
      }
    })();
  }, [request]);

  const cargoSelecionado = cargos.find((c) => c.id === dados.cargo_id);

  const validarPasso0 = () => {
    if (!dados.nome.trim()) return "Nome é obrigatório.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(dados.email)) return "Email inválido.";
    return null;
  };
  const validarPasso1 = () => {
    if (!dados.cargo_id) return "Selecione um cargo.";
    return null;
  };

  const avancar = () => {
    setError(null);
    let msg = null;
    if (passo === 0) msg = validarPasso0();
    if (passo === 1) msg = validarPasso1();
    if (msg) {
      setError(msg);
      return;
    }
    setPasso((p) => Math.min(2, p + 1));
  };

  const voltar = () => {
    setError(null);
    setPasso((p) => Math.max(0, p - 1));
  };

  const criar = async () => {
    setCriando(true);
    setError(null);
    try {
      const payload = {
        nome: dados.nome,
        email: dados.email,
        // Backend UsuarioCreateSerializer espera o campo `cargo` (PK), nao `cargo_id`.
        cargo: dados.cargo_id,
        cargo_exibido: dados.cargo_exibido || dados.nome,
      };
      const resp = await criarUsuario(request, payload);
      const usuario = resp?.data || resp;
      const senhaProvisoria = usuario?.senha_provisoria;
      // Se a opcao for "convite por email", dispara o envio aqui mesmo.
      // O caller continua tendo a senha provisoria como fallback.
      let convite = null;
      if (dados.enviar_convite && usuario?.id) {
        try {
          convite = await enviarConvite(request, usuario.id);
        } catch (err) {
          // Se o email falhar, mantemos a senha provisoria como saida.
          convite = { erro: err?.message || "Falha ao enviar convite." };
        }
      }
      setResultado({ usuario, senhaProvisoria, convite });
    } catch (err) {
      setError(err?.message || "Erro ao criar usuário.");
    } finally {
      setCriando(false);
    }
  };

  if (resultado) {
    return (
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Usuário criado</CardTitle>
            <CardSubtitle>
              {resultado.usuario?.nome} ({resultado.usuario?.email})
            </CardSubtitle>
          </div>
        </CardHeader>
        {resultado.convite?.data?.email_enviado ? (
          <InfoMsg>
            <FiMail style={{ verticalAlign: "middle" }} /> Convite enviado por
            email. Expira em 24h.
          </InfoMsg>
        ) : resultado.convite?.erro ? (
          <ErrorMsg>{resultado.convite.erro}</ErrorMsg>
        ) : null}
        {resultado.senhaProvisoria && (
          <InfoMsg>
            <strong>Senha provisória:</strong>{" "}
            <code style={{ background: "var(--surface-2)", padding: "2px 6px" }}>
              {resultado.senhaProvisoria}
            </code>{" "}
            <SecondaryButton
              type="button"
              onClick={() => navigator.clipboard?.writeText(resultado.senhaProvisoria)}
            >
              <FiCopy size={14} /> Copiar
            </SecondaryButton>
            <p style={{ marginTop: 8, opacity: 0.8 }}>
              Repasse com segurança ao usuário. Esta senha NÃO será mostrada de
              novo.
            </p>
          </InfoMsg>
        )}
        <ActionsBar>
          <PrimaryButton
            type="button"
            onClick={() =>
              resultado.usuario?.id &&
              navigate(`/configuracoes/acessos/usuarios/${resultado.usuario.id}`)
            }
          >
            Ir para ficha do usuário
          </PrimaryButton>
          <SecondaryButton type="button" onClick={() => navigate("/configuracoes/acessos/usuarios")}>
            Voltar para lista
          </SecondaryButton>
        </ActionsBar>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Novo usuário</CardTitle>
          <CardSubtitle>Configure dados, cargo e como entregar acesso.</CardSubtitle>
        </div>
        <SecondaryButton type="button" onClick={() => navigate("/configuracoes/acessos/usuarios")}>
          <FiArrowLeft size={14} /> Cancelar
        </SecondaryButton>
      </CardHeader>

      <StepIndicator atual={passo} />

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {passo === 0 && (
        <InlineForm onSubmit={(e) => { e.preventDefault(); avancar(); }}>
          <Field>
            Nome completo
            <Input
              autoFocus
              value={dados.nome}
              onChange={(e) => setDados((p) => ({ ...p, nome: e.target.value }))}
            />
          </Field>
          <Field>
            Email
            <Input
              type="email"
              value={dados.email}
              onChange={(e) => setDados((p) => ({ ...p, email: e.target.value }))}
            />
          </Field>
          <Field className="full">
            Cargo exibido (opcional)
            <Input
              placeholder="Como aparece no header. Padrão: o nome."
              value={dados.cargo_exibido}
              onChange={(e) => setDados((p) => ({ ...p, cargo_exibido: e.target.value }))}
            />
          </Field>
          <ActionsBar className="full" style={{ justifyContent: "flex-end" }}>
            <PrimaryButton type="submit">
              Próximo <FiArrowRight size={14} />
            </PrimaryButton>
          </ActionsBar>
        </InlineForm>
      )}

      {passo === 1 && (
        <InlineForm onSubmit={(e) => { e.preventDefault(); avancar(); }}>
          <Field className="full">
            Cargo
            <Select
              value={dados.cargo_id}
              onChange={(e) => setDados((p) => ({ ...p, cargo_id: e.target.value }))}
            >
              <option value="">Selecione...</option>
              {cargos.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </Select>
          </Field>
          {cargoSelecionado && (
            <div className="full">
              <CardSubtitle>
                Cargo selecionado: <strong>{cargoSelecionado.nome}</strong>
                {cargoSelecionado.exige_mfa
                  ? " — usuário será obrigado a configurar MFA."
                  : ""}
              </CardSubtitle>
            </div>
          )}
          <ActionsBar className="full" style={{ justifyContent: "space-between" }}>
            <SecondaryButton type="button" onClick={voltar}>
              <FiArrowLeft size={14} /> Voltar
            </SecondaryButton>
            <PrimaryButton type="submit">
              Próximo <FiArrowRight size={14} />
            </PrimaryButton>
          </ActionsBar>
        </InlineForm>
      )}

      {passo === 2 && (
        <Blocks>
          <Card style={{ border: "1px dashed" }}>
            <CardSubtitle>Confirme os dados:</CardSubtitle>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>
                <strong>Nome:</strong> {dados.nome}
              </li>
              <li>
                <strong>Email:</strong> {dados.email}
              </li>
              <li>
                <strong>Cargo:</strong> {cargoSelecionado?.nome}
              </li>
            </ul>
          </Card>
          <Card style={{ border: "1px dashed" }}>
            <CardTitle>Como entregar o acesso?</CardTitle>
            <ActionsBar>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <input
                  type="radio"
                  checked={dados.enviar_convite}
                  onChange={() => setDados((p) => ({ ...p, enviar_convite: true }))}
                />
                Enviar convite por email (recomendado)
              </label>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <input
                  type="radio"
                  checked={!dados.enviar_convite}
                  onChange={() => setDados((p) => ({ ...p, enviar_convite: false }))}
                />
                Gerar senha provisória
              </label>
            </ActionsBar>
            <CardSubtitle>
              {dados.enviar_convite
                ? "O usuário receberá um link único (24h) para definir a senha."
                : "Uma senha temporária será gerada e exibida UMA vez. Repasse com segurança."}
            </CardSubtitle>
          </Card>
          <ActionsBar style={{ justifyContent: "space-between" }}>
            <SecondaryButton type="button" onClick={voltar} disabled={criando}>
              <FiArrowLeft size={14} /> Voltar
            </SecondaryButton>
            <PrimaryButton type="button" onClick={criar} disabled={criando}>
              <FiCheckCircle size={14} /> Criar usuário
            </PrimaryButton>
          </ActionsBar>
        </Blocks>
      )}
    </Card>
  );
}

export default UsuarioNovo;
